import React, { useEffect } from 'react';

import type { TableFilter } from 'components/GMetrixTable/components/toolbar/components';
import { GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { buildExportFileName, GenerateUUID } from 'utils';
import { Certificate, StudentApi } from 'apis/StudentApi';
import { useNotification } from 'app/notifications';
import { useSuperState } from 'hooks';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';

import 'pages/students/components/Certificates/styles/Certificates.scss';

interface CertificatesProps {
    context?: string;
    id: number | undefined;
}

export const Certificates: React.FC<CertificatesProps> = ({ id, context = 'student' }) => {

    const [state, setState] = useSuperState({
        certificates: [] as Certificate[],
        dateRange: {
            end: new Date(),
            start: new Date(new Date().setDate(new Date().getDate() - 3650))
        } as { end: Date | null; start: Date | null },
        downloadingAll: false,
        filteredCertificates: [] as Certificate[],
        loading: false,
        searchTerm: '',
        selectedIssuedVia: 'All',
        selectedVendor: 'All',
        tableKey: 'CertificatesTable'
    });

    // const [selectedCertificates, setSelectedCertificates] = useState<Certificate[]>([]);
    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    // Load certificates data
    useEffect(() => {

        loadCertificates();

    }, [id]);

    // Apply filters whenever filter values change
    useEffect(() => {

        applyFilters();

    }, [state.certificates, state.selectedIssuedVia, state.selectedVendor, state.dateRange, state.searchTerm]);

    const loadCertificates = async () => {

        setState({ loading: true });
        try {

            let data: Certificate[] = [];

            if (id) {

                const response = await StudentApi.GetCertificateDetails(id, context);
                if (Array.isArray(response)) {

                    data = response.map(cert => ({
                        CertificateContent: cert.CertificateContent,
                        CertificateUrl: cert.CertificateUrl,
                        ClassroomAccessCode: cert.ClassroomAccessCode,
                        Course: cert.Course,
                        CourseStudentId: cert.CourseStudentId,
                        Id: cert.Id,
                        IssuedOn: new Date(cert.IssuedOn),
                        IssuedVia: cert.IssuedVia,
                        StudentName: cert.StudentName,
                        Vendor: cert.Vendor
                    }));

                }

            }

            setState({ certificates: data });

        } catch (error) {

            console.error('Error loading certificates:', error);
            setState({ certificates: [], filteredCertificates: [] });

        } finally {

            setState({ loading: false });

        }

    };

    const applyFilters = () => {

        let filtered = [...state.certificates];

        // Filter by Issued Via
        if (state.selectedIssuedVia !== 'All') {

            filtered = filtered.filter(cert => cert.IssuedVia === state.selectedIssuedVia);

        }

        // Filter by Vendor
        if (state.selectedVendor !== 'All') {

            filtered = filtered.filter(cert => cert.Vendor === state.selectedVendor);

        }

        // Filter by Date Range
        if (state.dateRange.start || state.dateRange.end) {

            filtered = filtered.filter(cert => {

                const issuedDate = new Date(cert.IssuedOn);
                const afterStart = !state.dateRange.start || issuedDate >= state.dateRange.start;
                const endOfDay = state.dateRange.end
                    ? new Date(state.dateRange.end.getFullYear(), state.dateRange.end.getMonth(), state.dateRange.end.getDate(), 23, 59, 59, 999)
                    : null;
                const beforeEnd = !endOfDay || issuedDate <= endOfDay;
                return afterStart && beforeEnd;

            });

        }

        // Filter by search term
        if (state.searchTerm) {

            const searchLower = state.searchTerm.toLowerCase();
            filtered = filtered.filter(cert => cert?.StudentName?.toLowerCase()?.includes(searchLower) ||
                cert?.Vendor?.toLowerCase()?.includes(searchLower) ||
                cert?.ClassroomAccessCode?.toLowerCase()?.includes(searchLower)
            );

        }
        setState({ filteredCertificates: filtered, tableKey: `CertificatesTable_${GenerateUUID()}` });

    };

    const buildCertificateFileName = (studentName: string): string => buildExportFileName('Lookup', `${studentName} Certificate`, 'pdf');

    const getUniqueZipFileName = (fileName: string, usedFileNames: Set<string>): string => {

        if (!usedFileNames.has(fileName)) {

            usedFileNames.add(fileName);
            return fileName;

        }

        const extensionIndex = fileName.lastIndexOf('.');
        const baseName = extensionIndex > -1 ? fileName.substring(0, extensionIndex) : fileName;
        const extension = extensionIndex > -1 ? fileName.substring(extensionIndex) : '';

        let counter = 2;
        let candidate = `${baseName}_${counter}${extension}`;

        while (usedFileNames.has(candidate)) {

            counter++;
            candidate = `${baseName}_${counter}${extension}`;

        }

        usedFileNames.add(candidate);
        return candidate;

    };

    // Get unique vendors from certificates
    const getAvailableVendors = (): string[] => {

        const vendors = new Set(state.filteredCertificates.map(cert => cert.Vendor));
        return Array.from(vendors).sort();

    };

    // Helper function to generate PDF blob from certificate
    const generatePdfBlob = async (certificate: Certificate): Promise<{ blob: Blob; filename: string } | null> => {

        try {

            if (!certificate.CertificateContent) {

                return null;

            }

            // Create a temporary container for the HTML content
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';
            container.style.padding = '20px';
            container.style.backgroundColor = '#ffffff';
            container.innerHTML = certificate.CertificateContent;
            document.body.appendChild(container);

            // Wait for any images to load
            const images = container.getElementsByTagName('img');
            await Promise.all(
                Array.from(images).map(img => {

                    if (img.complete) return Promise.resolve();
                    return new Promise(resolve => {

                        img.onload = resolve;
                        img.onerror = resolve;

                    });

                })
            );

            // Convert HTML to canvas
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                logging: false,
                scale: 2,
                useCORS: true
            });

            // Remove temporary container
            document.body.removeChild(container);

            // Create PDF with landscape orientation
            const pdf = new jsPDF({
                format: 'a4',
                orientation: 'landscape',
                unit: 'mm'
            });

            // Get PDF dimensions
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            // Calculate image dimensions to fit within PDF
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

            // Calculate final dimensions
            const finalWidth = imgWidth * ratio;
            const finalHeight = imgHeight * ratio;

            // Center the image on the page
            const x = (pdfWidth - finalWidth) / 2;
            const y = (pdfHeight - finalHeight) / 2;

            // Add image to PDF
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);

            const filename = buildCertificateFileName(certificate.StudentName);

            // Convert to blob
            const pdfBlob = pdf.output('blob');

            return { blob: pdfBlob, filename };

        } catch (error) {

            console.error('Error generating PDF for certificate:', certificate.Id, error);
            return null;

        }

    };

    // Handle download all certificates as zip
    const handleDownloadAll = async () => {

        if (state.filteredCertificates.length === 0) {

            createToast(formatMessage({ id: 'no_certificates_available_to_download.f55d8978', defaultMessage: 'No certificates available to download.' }), 'warning');
            return;

        }

        setState({ downloadingAll: true });
        try {

            const zip = new JSZip();
            const usedFileNames = new Set<string>();
            let successCount = 0;

            createToast(formatMessage({ id: 'generating_count_certificates.55f7708a', defaultMessage: 'Generating {count} certificate(s)...' }, { count: state.filteredCertificates.length }), 'info');

            // Generate PDFs for all certificates
            for (const certificate of state.filteredCertificates) {

                const result = await generatePdfBlob(certificate);
                if (result) {

                    const zipFileName = getUniqueZipFileName(result.filename, usedFileNames);
                    zip.file(zipFileName, result.blob);
                    successCount++;

                }

            }

            if (successCount === 0) {

                createToast(formatMessage({ id: 'failed_to_generate_any_certificates.e22bf092', defaultMessage: 'Failed to generate any certificates.' }), 'danger');
                setState({ downloadingAll: false });
                return;

            }

            // Generate zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Create download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(zipBlob);
            link.download = buildExportFileName('Lookup', 'Certificates', 'zip');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            createToast(formatMessage({ id: 'successfully_downloaded_count_certificat.f7639de0', defaultMessage: 'Successfully downloaded {count} certificate(s).' }, { count: successCount }), 'success');

        } catch (error) {

            console.error('Error downloading all certificates:', error);
            createToast(formatMessage({ id: 'failed_to_download_certificates_please_t.0f4cb0bd', defaultMessage: 'Failed to download certificates. Please try again.' }), 'danger');

        } finally {

            setState({ downloadingAll: false });

        }

    };

    // Handle download single certificate
    const handleDownloadCertificate = async (certificate: Certificate) => {

        try {

            const result = await generatePdfBlob(certificate);
            if (!result) {

                createToast(formatMessage({ id: 'this_certificate_could_not_be_generated.67f1c53e', defaultMessage: 'This certificate could not be generated. Please try again later or contact Support if the issue persists.' }), 'danger');
                return;

            }

            // Create download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(result.blob);
            link.download = result.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            setTimeout(() => URL.revokeObjectURL(link.href), 100);

        } catch (error) {

            console.error('Error downloading certificate:', error);
            createToast(formatMessage({ id: 'this_certificate_could_not_be_downloaded.fcca8ff1', defaultMessage: 'This certificate could not be downloaded. Please try again later or contact Support if the issue persists.' }), 'danger');

        }

    };
    const handleViewCertificate = async (certificate: Certificate) => {

        try {

            if (certificate.CertificateContent) {

                // Create a temporary container for the HTML content
                const container = document.createElement('div');
                container.style.position = 'absolute';
                container.style.left = '-9999px';
                container.style.top = '0';

                // container.style.width = '1000px';
                container.style.padding = '20px';
                container.style.backgroundColor = '#ffffff';
                container.innerHTML = certificate.CertificateContent;
                document.body.appendChild(container);

                // Wait for any images to load
                const images = container.getElementsByTagName('img');
                await Promise.all(
                    Array.from(images).map(img => {

                        if (img.complete) return Promise.resolve();
                        return new Promise(resolve => {

                            img.onload = resolve;
                            img.onerror = resolve;

                        });

                    })
                );

                // Convert HTML to canvas
                const canvas = await html2canvas(container, {
                    backgroundColor: '#ffffff',
                    logging: false,
                    scale: 2,
                    useCORS: true
                });

                // Remove temporary container
                document.body.removeChild(container);

                // Create PDF with landscape orientation
                const pdf = new jsPDF({
                    format: 'a4',
                    orientation: 'landscape',
                    unit: 'mm'
                });

                // Get PDF dimensions
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();

                // Calculate image dimensions to fit within PDF
                const imgWidth = canvas.width;
                const imgHeight = canvas.height;
                const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

                // Calculate final dimensions
                const finalWidth = imgWidth * ratio;
                const finalHeight = imgHeight * ratio;

                // Center the image on the page
                const x = (pdfWidth - finalWidth) / 2;
                const y = (pdfHeight - finalHeight) / 2;

                // Add image to PDF
                const imgData = canvas.toDataURL('image/jpeg', 1.0);
                pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);

                const filename = buildCertificateFileName(certificate.StudentName);

                // Convert to blob and create a File object with proper name
                const pdfBlob = pdf.output('blob');
                const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(pdfFile);

                // Open in new window with an HTML wrapper that sets the title
                const pdfWindow = window.open('', '_blank');

                if (!pdfWindow) {

                    createToast(formatMessage({ id: 'pop_up_blocked_please_allow_pop_ups_for.10c2cd95', defaultMessage: 'Pop-up blocked. Please allow pop-ups for this site.' }), 'warning');

                } else {

                    // Write HTML with embedded PDF viewer
                    pdfWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                        <head>
                            <title>${filename}</title>
                            <style>
                                body, html {
                                    margin: 0;
                                    padding: 0;
                                    height: 100%;
                                    overflow: hidden;
                                }
                                iframe {
                                    width: 100%;
                                    height: 100%;
                                    border: none;
                                }
                            </style>
                        </head>
                        <body>
                            <iframe src="${blobUrl}#view=FitH" type="application/pdf"></iframe>
                        </body>
                        </html>
                    `);
                    pdfWindow.document.close();

                }

            } else {

                // Fallback to opening the generation page
                const url = `/Courses/CourseCertificate.aspx?courseStudentId=${certificate.CourseStudentId}`;
                window.open(url, '_blank');

            }

        } catch (error) {

            console.error('Error viewing certificate:', error);
            createToast(formatMessage({ id: 'this_certificate_could_not_be_displayed.778d57bb', defaultMessage: 'This certificate could not be displayed. Please try again later or contact Support if the issue persists.' }), 'danger');

        }

    };

    const columns: GMetrixTableColumnProps<Certificate>[] = [
        ...(context !== 'student' ? [{
            dataType: 'String' as const,
            field: 'StudentName' as keyof Certificate,
            locked: true,
            title: formatMessage({ id: 'student_name.f4347b21', defaultMessage: 'Student Name' })
        }] : []),
        {
            dataType: 'String',
            field: 'Course',
            locked: context === 'student' ? false : true, // Lock course column for student context
            title: formatMessage({ id: 'course.790646f3', defaultMessage: 'Course' })
        },
        {
            dataType: 'String',
            field: 'Vendor',
            title: formatMessage({ id: 'vendor.b7db4ace', defaultMessage: 'Vendor' })
        },
        ...(context === 'student' ? [{
            dataType: 'String' as const,
            field: 'IssuedVia' as keyof Certificate,
            title: formatMessage({ id: 'issued_via.760a23b6', defaultMessage: 'Issued Via' })
        }] : []),
        ...(context === 'student' ? [{
            dataType: 'String' as const,
            field: 'ClassroomAccessCode' as keyof Certificate,
            locked: true,
            title: formatMessage({ id: 'classroom_access_code.b8fbd9fe', defaultMessage: 'Classroom / Access Code' })
        }] : []),
        {
            cell: (props: any) => (
                <td>
                    <FormattedDate day="numeric" month="short" value={new Date(props.dataItem.IssuedOn)} year="numeric" />
                </td>
            ),
            dataType: 'Date',
            field: 'IssuedOn',
            title: formatMessage({ id: 'issued_on.39fe5cd7', defaultMessage: 'Issued On' })
        },
        {
            cell: (props: any) => (
                <td>
                    <div className="d-flex">
                        <GMetrixButton
                            className="me-2"
                            iconClass="fa-solid fa-download"
                            onClick={() => handleDownloadCertificate(props.dataItem)}
                            size="sm"
                            variant="outline-primary"
                        >
                            <FormattedMessage defaultMessage="Download" id="download.5e1e7483" />
                        </GMetrixButton>
                        <GMetrixButton
                            onClick={() => handleViewCertificate(props.dataItem)}
                            size="sm"
                            variant="primary"
                        >
                            <FormattedMessage defaultMessage="View" id="view.1590505b" />
                        </GMetrixButton>
                    </div>
                </td>
            ),
            dataType: 'Actions',
            title: formatMessage({ id: 'actions.61fd1d29', defaultMessage: 'Actions' }),
            width: 225
        }
    ];

    const exportColumns = columns
        .filter(col => col.dataType !== 'Actions' && !!col.field)
        .map(col => ({
            field: String(col.field),
            title: col.title
        }));

    const filters: TableFilter[] = [
        ...(context === 'student' ? [{
            label: formatMessage({ id: 'issued_via.760a23b6', defaultMessage: 'Issued Via' }),
            options: [
                {
                    checked: state.selectedIssuedVia === 'All',
                    label: formatMessage({ id: 'all.6feedc20', defaultMessage: 'All' }),
                    onSelect: () => setState({ selectedIssuedVia: 'All' }),
                    value: 'All'
                },
                {
                    checked: state.selectedIssuedVia === 'Classroom',
                    label: formatMessage({ id: 'classroom.127e40cb', defaultMessage: 'Classroom' }),
                    onSelect: () => setState({ selectedIssuedVia: 'Classroom' }),
                    value: 'Classroom'
                },
                {
                    checked: state.selectedIssuedVia === 'Access Code',
                    label: formatMessage({ id: 'access_code.b5fc1b57', defaultMessage: 'Access Code' }),
                    onSelect: () => setState({ selectedIssuedVia: 'Access Code' }),
                    value: 'Access Code'
                }
            ],
            type: 'radio' as const
        }] : []),
        {
            label: formatMessage({ id: 'vendor.b7db4ace', defaultMessage: 'Vendor' }),
            options: [
                {
                    checked: state.selectedVendor === 'All',
                    label: formatMessage({ id: 'all.6feedc20', defaultMessage: 'All' }),
                    onSelect: () => setState({ selectedVendor: 'All' }),
                    value: 'All'
                },
                ...getAvailableVendors().map(vendor => ({
                    checked: state.selectedVendor === vendor,
                    label: vendor,
                    onSelect: () => setState({ selectedVendor: vendor }),
                    value: vendor
                }))
            ],
            type: 'radio' as const
        }
    ];

    const extraToolBarButtons = (
        <div className="d-flex flex-wrap align-items-end gap-3">
            {/* Clear Filters Button */}
            <div>
                <GMetrixButton
                    onClick={() => {

                        setState({
                            dateRange: {
                                end: new Date(),
                                start: new Date(new Date().setDate(new Date().getDate() - 30))
                            },
                            searchTerm: '',
                            selectedIssuedVia: 'All',
                            selectedVendor: 'All'
                        });

                    }}
                    variant="outline-danger"
                >
                    <FormattedMessage defaultMessage="Reset Filters" id="reset_filters.1504ae20" />
                </GMetrixButton>
            </div>

            {/* Download All Button */}
            <div>
                <GMetrixButton
                    disabled={state.downloadingAll || state.filteredCertificates.length === 0}
                    onClick={handleDownloadAll}
                    variant="outline-primary"
                >
                    {state.downloadingAll ? <FormattedMessage defaultMessage="Generating..." id="generating.5a538399" /> : <FormattedMessage defaultMessage="Download All" id="download_all.4d3d4285" />}
                </GMetrixButton>
            </div>
        </div>
    );

    return (
        <div className="certificates-container">
            <GMetrixTable

                dateFilter={{
                    endDate: state.dateRange.end,
                    maxDate: new Date(),
                    onChange: (start, end) => setState({ dateRange: { end, start } }),
                    startDate: state.dateRange.start
                }}

                // selectableRows={true}
                // multiSelect={true}
                // selected={selectedCertificates}
                // onSelectedChange={setSelectedCertificates}
                allowExport={true}
                bottomBarTableName={formatMessage({ id: 'certificates.03accec8', defaultMessage: 'Certificates' })}
                columnData={columns}
                defaultItemsPerPage={10}
                excelExportFileName={formatMessage({ id: 'studentcertificates.e398d354', defaultMessage: 'StudentCertificates' })}
                exportColumns={exportColumns}
                extraToolBarButtons={extraToolBarButtons}
                filters={filters}
                key={state.tableKey}
                loading={state.loading}
                noDataMessage={formatMessage({ id: 'no_certificates_found_for_this_student.3699e640', defaultMessage: 'No certificates found for this student.' })}
                rowData={state.filteredCertificates}
                searchPlaceholder={formatMessage({ id: 'search_certificates_by_name_or_type.33908b3a', defaultMessage: 'Search certificates by name or type...' })}
                searchTerm={state.searchTerm}
                setSearchTerm={searchTerm => setState({ searchTerm })}
                tableName={formatMessage({ id: 'certificates.03accec8', defaultMessage: 'Certificates' })}
                toggleColumns={true}
            />
        </div>
    );

};
